import { merge } from 'lodash'
import { Carousel, CarouselProps } from 'primereact/carousel'
import { useResizeListener } from 'primereact/hooks'
import React, { useEffect, useState } from 'react'

const responsiveOptions = [
  {
    breakpoint: '1800px',
    numVisible: 3,
    numScroll: 1,
  },
  {
    breakpoint: '1200px',
    numVisible: 2,
    numScroll: 1,
  },
  {
    breakpoint: '900px',
    numVisible: 1,
    numScroll: 1,
  },
]

const carouselBtnClassnames = 'bg-[var(--primary-color)] rounded-3xl text-white h-16 w-16'

export interface CommonLandingCarouselProps extends CarouselProps {
  carouselAriaLabel?: string
  containerAriaLabel?: string
  indicatorsAriaLabel?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  itemTemplate?: (item: any, hasOnlyTwoItems?: boolean) => React.ReactNode
}

export function CommonLandingCarousel(props: CommonLandingCarouselProps) {
  const [, setResizeEvt] = useState<{ width: number; height: number }>()

  const {
    carouselAriaLabel = 'Carousel di contenuti',
    containerAriaLabel = 'Elementi del carousel',
    indicatorsAriaLabel = 'Indicatori carousel',
    value,
    itemTemplate,
    ...carouselProps
  } = props

  // Controlla se ci sono solo 2 elementi
  const hasOnlyTwoItems = value && Array.isArray(value) && value.length === 2

  const [bindWindowResizeListener, unbindWindowResizeListener] = useResizeListener({
    listener: (event) => {
      setResizeEvt({
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        width: (event.currentTarget as any)?.innerWidth,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        height: (event.currentTarget as any)?.innerHeight,
      })
    },
  })

  useEffect(() => {
    bindWindowResizeListener()

    return () => {
      unbindWindowResizeListener()
    }
  }, [bindWindowResizeListener, unbindWindowResizeListener])

  if (hasOnlyTwoItems && itemTemplate) {
    return (
      <div className='flex flex-col items-center gap-4 lg:flex-row lg:gap-6'>
        {value.map((item, index) => (
          <div key={index} className='w-full lg:w-1/2'>
            {itemTemplate(item, true)}
          </div>
        ))}
      </div>
    )
  }

  return (
    <Carousel
      {...carouselProps}
      value={value}
      itemTemplate={(item) => itemTemplate?.(item, hasOnlyTwoItems)}
      pt={merge(carouselProps.pt, {
        root: {
          'aria-roledescription': 'carousel',
          'aria-label': carouselAriaLabel,
        },
        content: {
          'aria-live': 'polite',
          'aria-atomic': 'false',
        },
        container: {
          'aria-label': containerAriaLabel,
        },
        nextButtonIcon: {
          className: carouselBtnClassnames,
        },
        previousButtonIcon: { className: carouselBtnClassnames },
        indicator: {
          className: 'hidden lg:block',
        },
        nextButton: {
          'aria-label': 'Vai al prossimo elemento',
        },
        previousButton: {
          'aria-label': "Vai all'elemento precedente",
        },
        indicatorButton: {
          className: 'bg-[var(--primary-color)]',
          'aria-label': "Vai all'elemento",
        },
        indicators: {
          'aria-label': indicatorsAriaLabel,
        },
      })}
      numVisible={3}
      numScroll={1}
      responsiveOptions={responsiveOptions}
    />
  )
}
